import React, { useState, useRef, useEffect, useMemo } from 'react';
import countriesData from 'world-countries';
import * as Flags from 'country-flag-icons/react/3x2';

interface Country {
    value: string; // ISO 3166-1 alpha-2 code (e.g., "US", "GB")
    label: string; // Country name
}

interface CountrySelectorProps {
    value: string;
    onChange: (value: string) => void;
    className?: string;
    placeholder?: string;
}

export default function CountrySelector({ value, onChange, className = '', placeholder = 'Select country' }: CountrySelectorProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Transform world-countries data to our format
    const countries: Country[] = useMemo(() => {
        return countriesData
            .map((country) => ({
                value: country.cca2, // ISO 3166-1 alpha-2 code
                label: country.name.common, // Common name
            }))
            .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically
    }, []);

    // Filter countries based on search term
    const filteredCountries = useMemo(() => {
        if (!searchTerm) return countries;
        const term = searchTerm.toLowerCase();
        return countries.filter(country =>
            country.label.toLowerCase().includes(term) ||
            country.value.toLowerCase().includes(term)
        );
    }, [countries, searchTerm]);

    // Get selected country data
    const selectedCountry = countries.find(c => c.value === value);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSelect = (countryCode: string) => {
        onChange(countryCode);
        setIsOpen(false);
        setSearchTerm('');
    };

    const getFlag = (countryCode: string) => {
        try {
            // Convert country code to uppercase for the component name
            const code = countryCode.toUpperCase();
            // Get the flag component from the imported flags
            const FlagComponent = (Flags as any)[code];
            if (FlagComponent) {
                return <FlagComponent className="w-5 h-5" title={countryCode} />;
            }
            return <span className="w-5 h-5 flex items-center justify-center text-xs">🏳️</span>;
        } catch {
            return <span className="w-5 h-5 flex items-center justify-center text-xs">🏳️</span>;
        }
    };

    const inputStyle = "py-3 px-5 block w-full border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:pointer-events-none bg-white text-gray-900";

    return (
        <div className="relative" ref={dropdownRef} dir="ltr">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`${inputStyle} ${className} text-left flex items-center justify-between cursor-pointer`}
            >
                <div className="flex items-center gap-2">
                    {selectedCountry ? (
                        <>
                            {getFlag(selectedCountry.value)}
                            <span>{selectedCountry.label}</span>
                        </>
                    ) : (
                        <span className="text-gray-400">{placeholder}</span>
                    )}
                </div>
                <svg
                    className={`w-4 h-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
                    <div className="p-2 border-b border-gray-200">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search country..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            autoFocus
                        />
                    </div>
                    <div className="overflow-y-auto max-h-48">
                        {filteredCountries.length > 0 ? (
                            filteredCountries.map((country) => (
                                <button
                                    key={country.value}
                                    type="button"
                                    onClick={() => handleSelect(country.value)}
                                    className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-blue-50 transition-colors ${
                                        value === country.value ? 'bg-blue-100' : ''
                                    }`}
                                >
                                    {getFlag(country.value)}
                                    <span className="text-sm">{country.label}</span>
                                    {value === country.value && (
                                        <svg className="w-4 h-4 ml-auto text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </button>
                            ))
                        ) : (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">No countries found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

